import { MAX_SUBTITLE_LENGTH } from '../../../../constants/form';
import { BasicDetailsSectionBase } from '../../../dapp-form/sections/BasicDetailsSectionBase';
import { MediaUploadWithExisting } from '../../MediaUploadWithExisting';
import { PreviewUploadsField } from '../components/PreviewUploadsField';
import { PackageNameField } from '../components/PackageNameField';
import type { DashboardDetailsInputChangeEvent, FormState } from '../types';

interface BasicDetailsSectionProps {
  formData: FormState;
  previewDimensions: { width: number; height: number } | null;
  resolvedDappIconUrl: string | null;
  resolvedBannerUrl: string | null;
  resolvedPreviewUrls: (string | null)[];
  existingPreviewUrls: (string | null)[];
  onInputChange: (event: DashboardDetailsInputChangeEvent) => void;
  onSingleFileChange: (
    fieldName: 'dappIcon' | 'banner' | 'editorsChoiceGraphic',
    file: File | null,
  ) => Promise<void>;
  onPreviewChange: (index: number, file: File | null) => Promise<void>;
  onRemoveExistingDappIcon: () => void;
  onRemoveExistingBanner: () => void;
  onRemoveExistingPreview: (index: number) => void;
  onAddPreviewSlot: () => void;
  onRemovePreviewSlot: (index: number) => void;
}

export function BasicDetailsSection({
  formData,
  previewDimensions,
  resolvedDappIconUrl,
  resolvedBannerUrl,
  resolvedPreviewUrls,
  existingPreviewUrls,
  onInputChange,
  onSingleFileChange,
  onPreviewChange,
  onRemoveExistingDappIcon,
  onRemoveExistingBanner,
  onRemoveExistingPreview,
  onAddPreviewSlot,
  onRemovePreviewSlot,
}: BasicDetailsSectionProps) {
  return (
    <BasicDetailsSectionBase
      dappName={formData.dappName}
      subtitle={formData.subtitle}
      description={formData.description}
      subtitleHintText={`${formData.subtitle.length} / ${MAX_SUBTITLE_LENGTH} characters`}
      onInputChange={onInputChange}
      packageNameField={<PackageNameField value={formData.packageName} />}
      uploadFields={
        <div className="self-stretch flex flex-col md:flex-row gap-6">
          <MediaUploadWithExisting
            name="dappIcon"
            label="dApp Icon"
            description="The dApp icon appears in lists, detail page, and throughout the Seeker device. (Required)"
            dimensions="512x512px"
            value={formData.dappIcon}
            existingUrl={resolvedDappIconUrl}
            onChange={(file: File | null) =>
              onSingleFileChange('dappIcon', file)
            }
            onRemoveExisting={onRemoveExistingDappIcon}
            buttonType="upload"
            buttonClassName="bg-teal-600 text-white rounded-lg"
            placeholderClassName="w-24 h-24"
          />
          <MediaUploadWithExisting
            name="banner"
            label="Banner"
            description="This appears at the top of the detail page, behind the dApp icon."
            dimensions="1200x600px"
            value={formData.banner}
            existingUrl={resolvedBannerUrl}
            onChange={(file: File | null) => onSingleFileChange('banner', file)}
            onRemoveExisting={onRemoveExistingBanner}
            buttonType="add"
            buttonClassName="text-white"
            placeholderClassName="w-44 h-24"
          />
        </div>
      }
      previewUploadsField={
        <PreviewUploadsField
          dappPreviews={formData.dappPreviews}
          previewDimensions={previewDimensions}
          existingPreviewUrls={existingPreviewUrls}
          resolvedPreviewUrls={resolvedPreviewUrls}
          onPreviewChange={onPreviewChange}
          onRemoveExistingPreview={onRemoveExistingPreview}
          onAddPreviewSlot={onAddPreviewSlot}
          onRemovePreviewSlot={onRemovePreviewSlot}
        />
      }
    />
  );
}
